/**
 * AgendaTodayWidget — 149-cl → 149-cs (PROMPT 16-R2A-R2).
 *
 * 149-cs: SINGLE SOURCE OF TRUTH fix — sebelumnya widget iterasi `data.events`
 * (parent events) → overcount karena event dengan `occurrence_overrides` yg sudah
 * done pada per-occurrence tetap terhitung (kasus: 3 overdue di Dashboard vs 1
 * di Reminder Center). Sekarang iterasi `data.occurrences` (per-day expanded
 * scheduled/done/cancelled) — sinkron dgn RemindersPage `filtered` logic.
 *
 * Timezone: Asia/Jakarta via `computeReminderStatus` shared util (149-cq).
 * Deep-link: /info-agenda?tab=reminder&status=overdue|today (bukan /reminders lama).
 */
import { useEffect, useMemo, useState } from "react";
import { CalendarClock, AlertTriangle, ArrowRight, CheckCircle2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate } from "@/lib/constants";

const JKT_TZ = "Asia/Jakarta";
function jktToday() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: JKT_TZ }).format(new Date());
}

export default function AgendaTodayWidget() {
  const nav = useNavigate();
  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const today = jktToday();
        // Window: -30 hari (overdue) → +1 hari (hari ini). Backend expand
        // occurrences dan drop yg sudah 'done'/'cancelled' bisa di-filter di client.
        const [yy, mm, dd] = today.split("-").map(Number);
        const t = new Date(Date.UTC(yy, mm - 1, dd));
        const fromD = new Date(t); fromD.setUTCDate(fromD.getUTCDate() - 30);
        const toD = new Date(t);   toD.setUTCDate(toD.getUTCDate() + 1);
        const iso = (d) => `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
        const { data } = await api.get("/reminders/events", {
          params: { from: iso(fromD), to: iso(toD), include_occurrences: true },
        });
        if (cancelled) return;
        // 149-cs: HARUS pakai `occurrences` (bukan `events`). Occurrence sudah
        // membawa status per-occurrence — mirror logic RemindersPage L120-127.
        const occs = Array.isArray(data) ? [] : (data?.occurrences || []);
        setOccurrences(occs);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Gagal muat agenda");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const { overdue, today, filtered } = useMemo(() => {
    const jkt = jktToday();
    const overdue = occurrences.filter((o) => o.status === "scheduled" && (o.occurrence_date || "").slice(0, 10) < jkt);
    const today = occurrences.filter((o) => o.status === "scheduled" && (o.occurrence_date || "").slice(0, 10) === jkt);
    return { overdue, today, filtered: [...overdue, ...today] };
  }, [occurrences]);

  return (
    <section className="bg-white border border-slate-200" data-testid="section-agenda-today">
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-indigo-50">
        <div className="flex items-center gap-2">
          <CalendarClock size={16} className="text-indigo-700" />
          <h2 className="font-display text-lg tracking-tight text-[#0B1D3A]">Agenda &amp; Reminder Hari Ini</h2>
          <span data-testid="agenda-count" className={`px-2 py-0.5 text-[10px] font-bold tabular-nums ${filtered.length > 0 ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-500"}`}>
            {filtered.length}
          </span>
          {overdue.length > 0 && (
            <button
              type="button"
              data-testid="agenda-overdue-badge"
              onClick={() => nav("/info-agenda?tab=reminder&status=overdue")}
              title="Buka daftar Terlambat"
              className="px-2 py-0.5 text-[10px] font-bold tabular-nums bg-rose-600 text-white flex items-center gap-1 hover:bg-rose-700"
            >
              <AlertTriangle size={10} /> {overdue.length} terlambat
            </button>
          )}
        </div>
        <button
          type="button"
          data-testid="link-reminders"
          onClick={() => nav(overdue.length > 0 ? "/info-agenda?tab=reminder&status=overdue" : "/info-agenda?tab=reminder&status=today")}
          className="text-xs uppercase tracking-wider text-[#FF5A1F] font-semibold flex items-center gap-1"
        >
          Lihat semua <ArrowRight size={12} />
        </button>
      </header>

      {loading && (
        <div className="p-6 text-center text-sm text-slate-400 italic" data-testid="agenda-loading">Memuat…</div>
      )}
      {error && !loading && (
        <div className="p-4 text-sm text-rose-700 bg-rose-50 border-t border-rose-200" data-testid="agenda-error">
          Gagal memuat: {error}
        </div>
      )}
      {!loading && !error && filtered.length === 0 && (
        <div className="p-6 text-center text-sm text-slate-500" data-testid="agenda-empty">
          <CheckCircle2 size={22} className="mx-auto text-emerald-500 mb-1" />
          Tidak ada agenda / reminder yang jatuh hari ini atau overdue.
        </div>
      )}
      {!loading && !error && filtered.length > 0 && (
        <ul className="divide-y divide-slate-100 max-h-64 overflow-y-auto">
          {overdue.slice(0, 8).map((r) => (
            <li key={`ov-${r.event_id}-${r.occurrence_date}`} data-testid={`agenda-overdue-${r.event_id}`}
                onClick={() => nav("/info-agenda?tab=reminder&status=overdue")}
                className="px-4 py-2 flex items-center gap-2 text-xs cursor-pointer hover:bg-rose-50/50">
              <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border border-rose-300 bg-rose-50 text-rose-700">Terlambat</span>
              <span className="flex-1 truncate text-[#0B1D3A]">{r.title || "(tanpa judul)"}</span>
              <span className="text-[10px] tabular-nums text-slate-500">{fmtDate(r.occurrence_date)}</span>
            </li>
          ))}
          {today.slice(0, 8).map((r) => (
            <li key={`td-${r.event_id}-${r.occurrence_date}`} data-testid={`agenda-today-${r.event_id}`}
                onClick={() => nav("/info-agenda?tab=reminder&status=today")}
                className="px-4 py-2 flex items-center gap-2 text-xs cursor-pointer hover:bg-indigo-50/50">
              <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border border-indigo-300 bg-indigo-50 text-indigo-700">Hari Ini</span>
              <span className="flex-1 truncate text-[#0B1D3A]">{r.title || "(tanpa judul)"}</span>
              <span className="text-[10px] tabular-nums text-slate-500">{fmtDate(r.occurrence_date)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
